import { useState } from "react";
import { toast } from "sonner";
import { ChevronDown } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { makeForm } from "@/lib/upload";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { defaultSlots, slotLabel } from "@/components/PrepSupplierDialog";

const dm = (s) => { const d = new Date(s); return `${d.getDate()}/${String(d.getMonth() + 1).padStart(2, "0")}`; };
const hm = (s) => { const d = new Date(s); return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`; };
export const dmy = (s) => s && s.slice(0, 10).split("-").reverse().join("/");
export const primary = "w-full md:w-auto md:min-w-[240px] h-12 md:h-11 px-5 rounded-[14px] navy-bg text-white text-[15px] font-semibold hover:opacity-90 transition-opacity";
export const second = "w-full md:w-auto h-12 md:h-11 px-5 rounded-[14px] border border-slate-300 bg-white text-[15px] font-semibold navy-text hover:bg-slate-50 transition-colors";
const CHIP = { grey: "bg-slate-100 text-slate-600", amber: "bg-amber-100 text-amber-800", green: "bg-emerald-100 text-emerald-700", sky: "bg-sky-100 text-sky-800" };
const ACCEPT = { fotografo: "image/*,video/*", home_staging: "image/*,video/*", planta: "image/*,application/pdf", perito_ce: "image/*,application/pdf" };

export function Chip({ tone, children, testid }) {
  return <span className={`shrink-0 px-2.5 py-0.5 rounded-full text-xs font-bold ${CHIP[tone]}`} data-testid={testid}>{children}</span>;
}

export function Row({ icon: Icon, title, chip, sub, children, testid, small }) {
  return (
    <div className={`flex gap-3 ${small ? "py-3" : "py-4"}`} data-testid={testid}>
      <Icon size={small ? 18 : 22} className="text-slate-500 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <span className={`${small ? "text-[15px]" : "text-[16px]"} font-semibold text-slate-900`}>{title}</span>{chip}
        </div>
        {sub && <div className="text-sm text-slate-500 mt-0.5" data-testid={`${testid}-sub`}>{sub}</div>}
        {children && <div className={`mt-3 ${small ? "-ml-[30px]" : "-ml-[34px]"} md:ml-0 flex flex-col md:flex-row md:flex-wrap gap-2`}>{children}</div>}
      </div>
    </div>
  );
}

export function reportChip(r) {
  const last = r.versions[r.versions.length - 1];
  if (r.status === "aberto") return [<Chip tone="green" testid="prep-report-status">Aberto {dm(last.opened_at)}</Chip>, `Enviado ${dm(last.sent_at)} · aberto ${dm(last.opened_at)} às ${hm(last.opened_at)}`];
  if (r.status === "enviado") return [<Chip tone="sky" testid="prep-report-status">Enviado {dm(last.sent_at)}</Chip>, `Enviado ${dm(last.sent_at)} por ${last.channel === "email" ? "email" : "WhatsApp"} · ainda não aberto`];
  return [<Chip tone="grey" testid="prep-report-status">Por enviar</Chip>, null];
}

export function kindChip(k, st) {
  if (st.status === "marcado") return [<Chip tone="amber" testid={`prep-${k}-status`}>Marcado</Chip>, `${st.contact?.name || ""} · ${slotLabel(st.scheduled_at)}`];
  if (st.status === "pedido") return [<Chip tone="sky" testid={`prep-${k}-status`}>Pedido em {dm(st.requested_at)}</Chip>, st.contact?.name];
  if (st.status === "recebidas") return [<Chip tone="green" testid={`prep-${k}-status`}>{k === "fotografo" ? "Recebidas" : "Recebido"}</Chip>, st.contact?.name];
  return [<Chip tone="grey" testid={`prep-${k}-status`}>Por pedir</Chip>, null];
}

export function Versions({ vs }) {
  const [open, setOpen] = useState(false);
  if (vs.length < 2) return null;
  return (
    <div className="mt-1">
      <button onClick={() => setOpen(!open)} className="text-sm text-slate-500 inline-flex items-center gap-1 hover:text-slate-800" data-testid="prep-report-versions-toggle">Versões ({vs.length}) <ChevronDown size={13} /></button>
      {open && <ul className="mt-1 text-sm text-slate-600 space-y-0.5" data-testid="prep-report-versions">{[...vs].reverse().map((v) => (
        <li key={v.n} data-testid={`prep-report-version-${v.n}`}>v{v.n} · enviado {dm(v.sent_at)} por {v.sent_by}{v.opened_at ? ` · aberto ${dm(v.opened_at)}` : " · não aberto"}</li>))}</ul>}
    </div>
  );
}

export function ScheduleDialog({ pid, k, st, onClose, onDone }) {
  const [at, setAt] = useState((st.slots || [])[0] || defaultSlots()[0]);
  const save = async () => {
    try { await api.post(`/processes/${pid}/prep/${k}/status`, { status: "marcado", at }); toast.success("Sessão marcada"); onDone(); onClose(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="prep-schedule-dialog">
        <DialogHeader><DialogTitle className="font-heading text-ec-green">Data e hora marcadas</DialogTitle></DialogHeader>
        <div className="flex flex-wrap gap-2">{(st.slots || []).map((sl) => (
          <button key={sl} onClick={() => setAt(sl)} className={`h-10 px-3 rounded-full border text-sm ${at === sl ? "border-emerald-400 bg-emerald-50 text-emerald-700 font-semibold" : "border-slate-200"}`} data-testid={`prep-schedule-opt-${sl}`}>{slotLabel(sl)}</button>))}</div>
        <input type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)} className="w-full h-11 px-3 rounded-[12px] border border-slate-200" data-testid="prep-schedule-input" />
        <button onClick={save} disabled={!at} className={primary} data-testid="prep-schedule-save">Guardar</button>
      </DialogContent>
    </Dialog>
  );
}

export function UploadBtn({ pid, k, label, onDone }) {
  const [busy, setBusy] = useState(false);
  const pick = async (files) => {
    setBusy(true);
    try {
      for (const f of files) await api.post(`/processes/${pid}/prep/${k}/media`, await makeForm([["file", f]]));
      toast.success(files.length > 1 ? `${files.length} ficheiros carregados` : "Ficheiro carregado");
      onDone();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); onDone(); }
    finally { setBusy(false); }
  };
  return (
    <label className={`${second} inline-flex items-center justify-center cursor-pointer`} data-testid={`prep-${k}-upload-btn`}>
      <input type="file" multiple accept={ACCEPT[k]} className="sr-only" onChange={(e) => e.target.files?.length && pick([...e.target.files])} data-testid={`prep-${k}-upload-input`} />
      {busy ? "A carregar…" : label}
    </label>
  );
}

export async function downloadIcs(pid, k) {
  try {
    const { data } = await api.get(`/processes/${pid}/prep/${k}/calendar.ics`, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(data); a.download = `${k}.ics`; a.click();
  } catch (e) { toast.error("Não foi possível criar o evento."); }
}
